/* Shared bits for the company-first official-route cards (Blue-Collar). */
import React from 'react';
import { ExternalLink } from 'lucide-react';

export const ROLE_PREVIEW_NOTICE =
  'This is a work preview, not a live opening. Confirm your city, current role/category, capacity, documents, tools/vehicle needs and terms in the official company flow.';
export const CITY_CARD_NOTICE =
  'Customer service is verified in this city. Confirm current worker capacity, role, documents, costs and terms in the official company flow.';
export const EMPLOYER_CHECK_TEXT = 'Check current openings directly with the company.';

const VEHICLE_LABEL = {
  YES: 'Vehicle required — confirm type, papers and costs',
  YES_OR_CITY_SPECIFIC_OPTION_ONLY: 'Vehicle required (or a city-specific rental option, only if offered)',
  YES_OR_CONFIRM_IN_OFFICIAL_FLOW: 'Vehicle likely required — confirm in the official flow',
  CONFIRM_IN_OFFICIAL_FLOW: 'Vehicle need: confirm in the official flow',
  NO_GENERAL_VEHICLE_REQUIREMENT_STATED: 'No general vehicle requirement stated',
  NOT_STATED_AS_GENERAL_REQUIREMENT: 'Not stated as a general requirement',
  NOT_ASSESSED: 'Not assessed',
};

export const humanize = (s) => {
  if (!s) return '';
  const t = String(s).replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};
export const vehicleLabel = (s) => VEHICLE_LABEL[s] || humanize(s);

export const OfficialLink = ({ href, label, tone = 'emerald', testId, small }) => {
  if (!href) return null;
  const tones = {
    emerald: 'bg-emerald-700 hover:bg-emerald-800 text-white focus-visible:ring-emerald-500',
    amber: 'bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-500',
    outline: 'border-2 border-gray-200 text-gray-800 hover:border-emerald-400 bg-white focus-visible:ring-emerald-500',
  };
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 rounded-full font-extrabold transition-colors focus-visible:ring-2 ${
        small ? 'min-h-[36px] px-3.5 py-1.5 text-xs' : 'min-h-[44px] px-5 py-2.5 text-sm'
      } ${tones[tone]}`}
      data-testid={testId}>
      {label} <ExternalLink size={small ? 12 : 14} />
    </a>
  );
};

export const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">{label}</p>
    <div className="text-xs text-gray-800 mt-0.5 leading-relaxed">{children}</div>
  </div>
);

/* 16:9 cover image — WebP delivery with the original PNG as fallback, lazy unless eager. */
export const CoverImg = ({ src, alt, eager = false, className = '', testId }) => {
  if (!src) return null;
  const webp = src.replace(/\.png$/i, '.webp');
  const hasWidth = /(^|\s)w-/.test(className);
  return (
    <picture className={`block ${hasWidth ? '' : 'w-full'} max-w-full ${className}`} data-testid={testId}>
      {webp !== src && <source srcSet={webp} type="image/webp" />}
      <img
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager ? 'high' : 'auto'}
        className="w-full h-full object-cover"
        style={{ aspectRatio: '16 / 9' }}
      />
    </picture>
  );
};
